import React, { useState } from 'react';
import { usePower } from '../state/PowerContext';
import { SOUNDS, playAlertSound, listCustomSounds, addCustomSound, removeCustomSound } from '../utils/alertSound';
import { notifyDiscord } from '../api';

const MAX_SOUND_BYTES = 3 * 1024 * 1024;

export default function SettingsModal({ onClose }) {
  const { alerts, updateAlerts } = usePower();
  const [testDiscordStatus, setTestDiscordStatus] = useState(null);
  const [customSounds, setCustomSounds] = useState(listCustomSounds);
  const [soundErr, setSoundErr] = useState('');

  const set = (patch) => updateAlerts(patch);
  const setSound = (patch) => updateAlerts({ sound: { ...alerts.sound, ...patch } });
  const setBrowser = (patch) => updateAlerts({ browser: { ...alerts.browser, ...patch } });
  const setDiscord = (patch) => updateAlerts({ discord: { ...alerts.discord, ...patch } });
  const setWeather = (patch) => updateAlerts({ weather: { ...alerts.weather, ...patch } });

  const uploadSound = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setSoundErr('');
    if (!file.type.startsWith('audio/') && !/\.(mp3|wav|ogg|m4a)$/i.test(file.name)) {
      setSoundErr('not an audio file — use .wav or .mp3');
      return;
    }
    if (file.size > MAX_SOUND_BYTES) {
      setSoundErr('file too big — keep alert sounds under 3 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const s = addCustomSound(file.name, reader.result);
        setCustomSounds(listCustomSounds());
        setSound({ name: `custom:${s.id}` });
      } catch (err) {
        setSoundErr(err.message);
      }
    };
    reader.onerror = () => setSoundErr('could not read file');
    reader.readAsDataURL(file);
  };

  const deleteSelectedCustom = () => {
    removeCustomSound(alerts.sound.name.slice(7));
    setCustomSounds(listCustomSounds());
    setSound({ name: 'chime' });
  };

  const testBrowserNotification = () => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') {
      Notification.requestPermission().catch(() => {});
      return;
    }
    try {
      new Notification('Peak Watch — test', {
        body: 'This is what a peak-risk browser alert looks like.',
        requireInteraction: !!alerts.browser.requireInteraction,
      });
    } catch {}
  };

  const testDiscord = async () => {
    if (!alerts.discord.webhookUrl) return;
    setTestDiscordStatus('sending…');
    try {
      await notifyDiscord(
        alerts.discord.webhookUrl,
        '✅ Peak Watch test message — alerts are wired up.',
        alerts.discord.mentionUserId,
      );
      setTestDiscordStatus('sent ✓');
    } catch (e) {
      setTestDiscordStatus('failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 560 }}>
        <h2>Alert settings</h2>

        <div className="settings-row">
          <label>
            <input type="checkbox" checked={alerts.enabled} onChange={e => set({ enabled: e.target.checked })} />
            {' '}Alerts enabled
          </label>
        </div>

        <fieldset className="settings-group">
          <legend>Trigger</legend>
          <div className="settings-row">
            <label>Alert on</label>
            <select value={alerts.triggerLevel || 'danger'} onChange={e => set({ triggerLevel: e.target.value })}>
              <option value="danger">Red blocks only (≥95% — danger of a new high)</option>
              <option value="warning">Amber + red (≥90%)</option>
              <option value="custom">Custom threshold…</option>
            </select>
            <span className="hint">"red only" alerts exactly when red blocks are on the grid</span>
          </div>
          {alerts.triggerLevel === 'custom' && (
            <div className="settings-row">
              <label>Threshold (% of period high)</label>
              <input type="number" min={50} max={100} step={1}
                value={alerts.thresholdPct}
                onChange={e => set({ thresholdPct: Math.max(50, Math.min(100, parseInt(e.target.value, 10) || 96)) })} />
              <span className="hint">warn when any upcoming hour ≥ this % of the current high (96 = within 4%)</span>
            </div>
          )}
          <div className="settings-row">
            <label>Lead time (hours)</label>
            <input type="number" min={0} max={48} step={1}
              value={alerts.leadTimeHours}
              onChange={e => set({ leadTimeHours: Math.max(0, Math.min(48, parseInt(e.target.value, 10) || 12)) })} />
            <span className="hint">how early to start alerting</span>
          </div>
          <div className="settings-row">
            <label>Reminder interval (minutes)</label>
            <input type="number" min={5} max={240} step={5}
              value={alerts.reminderMinutes}
              onChange={e => set({ reminderMinutes: Math.max(5, Math.min(240, parseInt(e.target.value, 10) || 60)) })} />
            <span className="hint">how often to re-nag until the hour passes</span>
          </div>
        </fieldset>

        <fieldset className="settings-group">
          <legend>Weather-inferred risks</legend>
          <div className="settings-row">
            <label><input type="checkbox" checked={alerts.weather.includeInAlerts} onChange={e => setWeather({ includeInAlerts: e.target.checked })} /> Fire alerts from weather-inferred risk</label>
            <span className="hint">purple 🌡 chips — past MISO's 5-day horizon, based on historical temp→power lag</span>
          </div>
          <div className="settings-row">
            <label>Max days ahead</label>
            <input type="number" min={1} max={15} step={1}
              value={alerts.weather.maxDaysAhead}
              onChange={e => setWeather({ maxDaysAhead: Math.max(1, Math.min(15, parseInt(e.target.value, 10) || 10)) })} />
            <span className="hint">ignore weather-inferred risk beyond this horizon (further-out forecasts get less reliable)</span>
          </div>
          <div className="settings-row">
            <label>Alert threshold (% of period high)</label>
            <input type="number" min={80} max={200} step={1}
              value={alerts.weather.thresholdPct}
              onChange={e => setWeather({ thresholdPct: Math.max(80, Math.min(200, parseInt(e.target.value, 10) || 100)) })} />
            <span className="hint">only alert when the historical-next-hour max ≥ this % of current period high</span>
          </div>
        </fieldset>

        <fieldset className="settings-group">
          <legend>Channels (while this tab is open)</legend>
          <div className="settings-row">
            <label><input type="checkbox" checked={alerts.tabFlash} onChange={e => set({ tabFlash: e.target.checked })} /> Flash browser tab title</label>
          </div>
          <div className="settings-row">
            <label><input type="checkbox" checked={alerts.sound.enabled} onChange={e => setSound({ enabled: e.target.checked })} /> Play sound</label>
            <select value={alerts.sound.name} onChange={e => setSound({ name: e.target.value })}>
              {Object.entries(SOUNDS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              {customSounds.map(s => <option key={s.id} value={`custom:${s.id}`}>🎵 {s.label}</option>)}
            </select>
            <input type="range" min={0} max={1} step={0.05}
              value={alerts.sound.volume}
              onChange={e => setSound({ volume: parseFloat(e.target.value) })}
              style={{ width: 100 }} />
            <button onClick={() => playAlertSound(alerts.sound.name, alerts.sound.volume)}>Test</button>
          </div>
          <div className="settings-row">
            <label>Custom sound</label>
            <input type="file" accept="audio/*,.mp3,.wav" onChange={uploadSound} style={{ maxWidth: 240 }} />
            {alerts.sound.name.startsWith('custom:') && (
              <button onClick={deleteSelectedCustom} title="Remove the selected custom sound">Delete selected</button>
            )}
            {soundErr
              ? <span className="hint" style={{ color: 'var(--danger)' }}>{soundErr}</span>
              : <span className="hint">.wav or .mp3, under 3 MB — stored in this browser only</span>}
          </div>
        </fieldset>

        <fieldset className="settings-group">
          <legend>Browser notifications (OS-level)</legend>
          <div className="settings-row">
            <label><input type="checkbox" checked={alerts.browser.enabled} onChange={e => setBrowser({ enabled: e.target.checked })} /> Enabled</label>
            {'Notification' in window && (
              <span className="hint">
                permission: <strong>{Notification.permission}</strong>
                {Notification.permission !== 'granted' &&
                  <button style={{ marginLeft: 8 }} onClick={() => Notification.requestPermission()}>Request</button>}
              </span>
            )}
            <button onClick={testBrowserNotification}>Test</button>
          </div>
          <div className="settings-row">
            <label><input type="checkbox" checked={alerts.browser.requireInteraction} onChange={e => setBrowser({ requireInteraction: e.target.checked })} /> Stay on screen until dismissed</label>
          </div>
          <div className="settings-row">
            <label><input type="checkbox" checked={alerts.browser.repeatWithReminders} onChange={e => setBrowser({ repeatWithReminders: e.target.checked })} /> Repeat with each reminder</label>
            <span className="hint">re-notify every reminder interval, not just on first detection</span>
          </div>
          <div className="settings-row">
            <label><input type="checkbox" checked={alerts.browser.includeWeather} onChange={e => setBrowser({ includeWeather: e.target.checked })} /> Include weather-inferred risks</label>
            <span className="hint">purple 🌡 alerts also pop OS notifications</span>
          </div>
        </fieldset>

        <fieldset className="settings-group">
          <legend>Discord (works even when tab is closed — if backend cron is running)</legend>
          <div className="settings-row">
            <label><input type="checkbox" checked={alerts.discord.enabled} onChange={e => setDiscord({ enabled: e.target.checked })} /> Send to Discord</label>
          </div>
          <div className="settings-row">
            <label>Webhook URL</label>
            <input type="text" style={{ flex: 1 }}
              placeholder="https://discord.com/api/webhooks/..."
              value={alerts.discord.webhookUrl}
              onChange={e => setDiscord({ webhookUrl: e.target.value.trim() })} />
          </div>
          <div className="settings-row">
            <label>Mention user ID (optional)</label>
            <input type="text" style={{ width: 220 }}
              placeholder="your 18-digit Discord user ID"
              value={alerts.discord.mentionUserId}
              onChange={e => setDiscord({ mentionUserId: e.target.value.trim() })} />
            <button onClick={testDiscord} disabled={!alerts.discord.webhookUrl}>Send test</button>
            {testDiscordStatus && <span className="hint" style={{ color: testDiscordStatus.startsWith('failed') ? 'var(--danger)' : 'var(--ok)' }}>{testDiscordStatus}</span>}
          </div>
          <div className="settings-row">
            <span className="hint" style={{ color: 'var(--text-dim)' }}>
              Create a webhook in your Discord server/channel (Settings → Integrations → Webhooks → New).
              To receive DMs instead, you'd need a full bot — not wired yet; for now use a private channel you alone see.
            </span>
          </div>
        </fieldset>

        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
